import { useEffect, useState } from "react";
import { Bell, BellOff, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { enableWebPush, disableWebPush, isPushSupported, currentPushPermission } from "@/lib/webpush";

/**
 * Small banner asking for browser notification permission once.
 * - Shown when logged-in, supported, and permission is "default"
 * - Stores dismissal in localStorage per user
 * - Once granted, registers SW + subscribes via VAPID
 */
export const PushPermissionBanner = () => {
  const { user, authHeader } = useAuth();
  const [permission, setPermission] = useState(() => (typeof window !== "undefined" ? currentPushPermission() : "default"));
  const [dismissed, setDismissed] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!user) return;
    const key = `ca_push_dismissed_${user.id}`;
    setDismissed(localStorage.getItem(key) === "1");
    // If already granted on this device, ensure subscription is registered
    if (currentPushPermission() === "granted") {
      enableWebPush(authHeader).catch(() => null);
    }
  }, [user, authHeader]);

  const enable = async () => {
    setWorking(true);
    const r = await enableWebPush(authHeader);
    setWorking(false);
    if (r.state === "subscribed") {
      toast.success("Notificaciones activadas en este dispositivo 🔔");
      setPermission("granted");
    } else if (r.state === "denied") {
      toast.error("Permiso denegado. Habilítalo en la configuración del navegador.");
      setPermission("denied");
    } else if (r.state === "unsupported") {
      toast.error("Tu navegador no soporta notificaciones push.");
    } else {
      toast.error("Error activando notificaciones");
    }
  };

  const dismiss = () => {
    if (user) localStorage.setItem(`ca_push_dismissed_${user.id}`, "1");
    setDismissed(true);
  };

  if (!user || !isPushSupported() || permission !== "default" || dismissed) return null;

  return (
    <div data-testid="push-banner" className="fixed top-20 right-4 z-[100] max-w-sm bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-2 border-[#d4af37]/40 rounded-2xl shadow-2xl shadow-amber-500/20 p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#d4af37] to-amber-600 flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-black" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-semibold text-sm mb-1">Activa las notificaciones</h4>
          <p className="text-[11px] text-neutral-300 leading-relaxed">
            Recibe avisos al instante en este dispositivo cuando tengas mensajes, citas, pagos o tu horóscopo diario.
          </p>
          <div className="flex gap-2 mt-2">
            <button
              data-testid="push-enable"
              onClick={enable}
              disabled={working}
              className="btn-gold px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest"
            >{working ? "Activando..." : "Activar"}</button>
            <button
              data-testid="push-dismiss"
              onClick={dismiss}
              className="text-[11px] text-neutral-400 hover:text-white"
            >Ahora no</button>
          </div>
        </div>
        <button data-testid="push-close" onClick={dismiss} className="text-neutral-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

/** Settings toggle for the My Account page — re-enable, or revoke push */
export const PushToggle = () => {
  const { authHeader } = useAuth();
  const [permission, setPermission] = useState(() => currentPushPermission());

  const enable = async () => {
    const r = await enableWebPush(authHeader);
    setPermission(currentPushPermission());
    if (r.state === "subscribed") toast.success("Notificaciones activadas");
    else if (r.state === "denied") toast.error("Permiso denegado. Habilita en el navegador.");
  };
  const disable = async () => {
    await disableWebPush(authHeader);
    toast.success("Notificaciones desactivadas en este dispositivo");
    setPermission(currentPushPermission());
  };

  if (!isPushSupported()) {
    return <div className="text-xs text-neutral-500">Tu navegador no soporta notificaciones push.</div>;
  }

  return (
    <div className="flex items-center gap-2">
      {permission === "granted" ? (
        <button data-testid="push-toggle-off" onClick={disable} className="text-xs px-3 py-1.5 rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1"><BellOff className="w-3 h-3" /> Desactivar notificaciones</button>
      ) : (
        <button data-testid="push-toggle-on" onClick={enable} className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 inline-flex items-center gap-1"><Bell className="w-3 h-3" /> Activar notificaciones</button>
      )}
      <span className="text-[10px] text-neutral-500">({permission})</span>
    </div>
  );
};
